<template>
  <div class="purchase-page">
    <h1>Оформить подписку</h1>
    <SubscriptionType class="tariff-list"
      :list="subscriptionTypes" :selectedId="selectedSubscriptionTypeId"
      @select="selectSubscriptionTypeHandler" />

    <div v-if="uuidValue" class="form-container">
      <div class="id-section">
        <label class="input-label" for="purchase-id">Ваш ID</label>
        <input id="purchase-id" class="input uuid-input" readonly :value="uuidValue"
          title="Нажмите, чтобы скопировать ID" @click="copyUuid" />
        <p class="field-hint">Нажмите на поле, чтобы скопировать ID</p>
      </div>

      <h2 class="payment-heading">Выберите способ оплаты</h2>
      <div class="payment-methods" role="group" aria-label="Способ оплаты">
        <button v-for="method in paymentMethods" :key="method.id" type="button"
          :class="['payment-card', { 'payment-card_selected': selectedPaymentMethod === method.id }]"
          :aria-pressed="selectedPaymentMethod === method.id" :disabled="isLoadingLink"
          @click="selectPaymentMethod(method.id)">
          <span class="payment-card__top">
            <span class="payment-card__icon" aria-hidden="true">{{ method.icon }}</span>
          </span>
          <span class="payment-card__title">{{ method.title }}</span>
          <span class="payment-card__description">{{ method.description }}</span>
        </button>
      </div>

      <div v-if="selectedPaymentMethod === 'tg'" class="payment-details">
        <p class="payment-summary">{{ selectedSubscription.title }} · {{ selectedSubscription.price_stars }} ⭐</p>
        <a :href="telegramUrl" class="payment-continue" target="_blank" rel="noopener noreferrer">
          Оплатить {{ selectedSubscription.price_stars }} ⭐ в Telegram
        </a>
        <p class="field-hint">Тариф и ID передадутся в бот. Там их можно изменить.</p>
        <p class="field-hint stars-hint">
          Нет звёзд? Их можно купить на
          <a href="https://ggsel.net/" target="_blank" rel="noopener noreferrer">ggsel.net</a>,
          <a href="https://donatov.net/" target="_blank" rel="noopener noreferrer">donatov.net</a>
          или <a href="https://plati.market/" target="_blank" rel="noopener noreferrer">plati.market</a>.
        </p>
      </div>

      <form v-if="selectedPaymentMethod === 'crypt'" class="payment-details"
        novalidate @submit.prevent="getCryptPayment">
        <div class="email-section">
          <label class="input-label" for="purchase-email">Email для чека</label>
          <input id="purchase-email" ref="emailInput" v-model.trim="email" type="email"
            autocomplete="email" placeholder="you@example.com" required
            :class="['input', { 'input_error': emailError }]"
            :aria-invalid="emailError ? 'true' : 'false'" aria-describedby="email-feedback"
            :disabled="isLoadingLink" @input="emailError = ''" />
          <p id="email-feedback" :class="['field-hint', { 'field-error': emailError }]" aria-live="polite">
            {{ emailError || 'Укажите почту для отправки чека.' }}
          </p>
        </div>
        <button type="submit" class="payment-continue" :disabled="isLoadingLink" :aria-busy="isLoadingLink">
          {{ isLoadingLink ? 'Получаем ссылку…' : 'Перейти к оплате криптовалютой' }}
        </button>
        <p class="field-hint">Оплата через Trybit. Итоговую сумму вы увидите перед оплатой.</p>
      </form>

      <div class="purchase-terms">
        <p><b>Примечание:</b> ID привязывается к железу ПК и Windows!
          При замене комплектующих ПК или переустановке Windows ID может измениться.
          Производите обновление ПК или переустановку Windows после окончания подписки.</p>
        <p>Продавец не несет ответственности в случае утери доступа к подписке из-за смены вашего ID.
          Продолжая оплату, вы соглашаетесь с вышеизложенным и с
          <router-link class="link" title="Условия использования" :to="{ name: 'Terms' }">условиями использования</router-link>
          и берете всю ответственность на себя.</p>
      </div>
    </div>

    <div v-else class="description section">
      <h2>Как оформить подписку?</h2>
      <p>Откройте программу и нажмите «Купить». На сайте автоматически подставится ваш ID.
        Выберите тариф и способ оплаты. Почта потребуется только для оплаты криптовалютой.</p>
    </div>
  </div>
</template>

<script>
import SubscriptionType from '@/components/SubscriptionType.vue';

export default {
  name: 'BuyKey',
  components: { SubscriptionType },
  metaInfo: {
    title: 'ВХ для КС - купить подписку',
    meta: [{ name: 'description', content: 'Купить подписку на ВХ чит для cs2 по доступной цене.' }],
    link: [{ rel: 'canonical', href: 'https://cssoftshop.app/buykey' }],
  },
  data() {
    return {
      isLoadingLink: false,
      email: '',
      emailError: '',
      selectedPaymentMethod: null,
      subscriptionTypes: [
        {
          id: 1, title: '14 дней', price: 300, price_usd: 3.6, price_stars: 300,
        },
        {
          id: 2, title: '30 дней', price: 450, price_usd: 5.5, price_stars: 450,
        },
        {
          id: 3, title: '60 дней', price: 800, price_usd: 9.8, price_stars: 800,
        },
      ],
      selectedSubscriptionTypeId: 2,
      paymentMethods: [
        {
          id: 'tg', title: 'Telegram Stars', icon: '★', description: 'Оплата звёздами в Telegram',
        },
        {
          id: 'crypt', title: 'Криптовалюта', icon: '₮', description: 'Через Trybit · понадобится email',
        },
      ],
    };
  },
  computed: {
    uuidValue() {
      return this.$route.params.uuid || '';
    },
    selectedSubscription() {
      return this.subscriptionTypes.find((item) => item.id === this.selectedSubscriptionTypeId);
    },
    telegramUrl() {
      // Telegram start payload: ASCII letters, digits, _ and -, no more than 64 characters.
      const payload = `p1_${this.uuidValue}_${this.selectedSubscriptionTypeId}`;
      if (!/^[A-Za-z0-9-]{1,56}$/.test(this.uuidValue)) return null;
      return `https://t.me/cssoftshop_pay_bot?start=${encodeURIComponent(payload)}`;
    },
  },
  created() {
    try {
      this.email = localStorage.getItem('user_email') || '';
    } catch (error) {
      // Покупка доступна и при отключённом localStorage.
    }
  },
  methods: {
    async copyUuid() {
      try {
        await navigator.clipboard.writeText(this.uuidValue);
        this.$notify.success({ title: 'ID скопирован', message: 'Вы можете вставить его в любое место' });
      } catch (error) {
        this.$notify.error({ title: 'Не удалось скопировать ID', message: 'Выделите и скопируйте ID вручную.' });
      }
    },
    selectPaymentMethod(id) {
      if (id === 'tg' && !this.telegramUrl) {
        this.$notify.error({ title: 'Некорректный ID', message: 'Откройте страницу покупки через кнопку «Купить» в программе.' });
        return;
      }
      this.selectedPaymentMethod = id;
      this.emailError = '';
    },
    async getCryptPayment() {
      if (this.isLoadingLink) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.emailError = 'Введите корректный email, чтобы продолжить оплату.';
        this.$refs.emailInput.focus();
        return;
      }
      try {
        localStorage.setItem('user_email', this.email);
      } catch (error) {
        // Ошибка сохранения почты не должна блокировать оплату.
      }
      this.isLoadingLink = true;
      try {
        const response = await this.$api.getPaymentData({
          uuid: this.uuidValue,
          promoCode: '',
          subscriptionType: this.selectedSubscriptionTypeId,
          email: this.email,
          type: 'crypt',
        });
        if (response.data.status === 'error') {
          this.$notify.error({ title: 'Не удалось открыть оплату', message: response.data.error });
          return;
        }
        const paymentUrl = new URL(response.data.paymentForm);
        if (paymentUrl.protocol !== 'https:') throw new Error('Invalid payment URL');
        // Переход в текущей вкладке не блокируется браузером после асинхронного запроса.
        window.location.assign(paymentUrl.href);
      } catch (error) {
        this.$notify.error({ title: 'Ошибка сети', message: 'Не удалось получить ссылку для оплаты. Попробуйте ещё раз.' });
      } finally {
        this.isLoadingLink = false;
      }
    },
    selectSubscriptionTypeHandler(id) {
      if (!this.isLoadingLink) this.selectedSubscriptionTypeId = id;
    },
  },
};
</script>

<style scoped lang="sass">
.purchase-page
  width: 900px
  max-width: 100%
.tariff-list
  margin-top: 24px
.form-container
  margin: 24px auto 32px
  max-width: 560px
.id-section
  max-width: 480px
  margin: 0 auto
.input-label
  display: block
  color: #fcfcfc
  font-size: 13px
  margin-bottom: 8px
.input
  box-sizing: border-box
  width: 100%
  min-width: 0
  padding: 12px 15px
  border: 1px solid #183485
  border-radius: 5px
  background: rgba(30, 30, 30, .9)
  color: white
  font-size: 15px
  &:focus
    outline: 2px solid #6790ff
    outline-offset: 2px
.uuid-input
  cursor: pointer
.input_error
  border-color: #ff9184
.field-hint
  margin-top: 9px
  font-size: 12px
  color: #c2c7d4
.stars-hint
  margin: 12px auto 0
  max-width: 460px
  line-height: 1.7
  a
    color: #a9e7b9 !important
    text-underline-offset: 3px
.field-error
  color: #ffb0a6
.payment-heading
  margin: 28px 0 14px
  font-size: 20px
.payment-methods
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 12px
.payment-card
  padding: 16px 18px
  border: 1px solid #183485
  border-radius: 5px
  background: rgba(30, 30, 30, .85)
  color: white
  text-align: left
  cursor: pointer
  transition: background .15s, border-color .15s
  &:hover
    background: rgba(50, 50, 50, .9)
  &:focus-visible
    outline: 2px solid #87a5ff
    outline-offset: 3px
  &:disabled
    cursor: wait
  &_selected, &_selected:hover
    background: #2854d5
    border-color: #6387ef
  &__top
    display: flex
    align-items: center
    justify-content: space-between
    margin-bottom: 12px
  &__icon
    font-size: 24px
    color: #8dea9b
  &__title
    display: block
    font-size: 18px
    font-weight: bold
  &__description
    display: block
    margin-top: 7px
    color: #e0e5f2
    font-size: 12px
    line-height: 1.5
.payment-details
  margin: 22px auto 0
.payment-summary
  margin-bottom: 12px
  font-size: 15px
.payment-continue
  display: block
  box-sizing: border-box
  width: 100%
  padding: 16px 20px
  border: 1px solid #67e787
  border-radius: 5px
  background: rgba(70, 155, 92, .18)
  color: white !important
  font-size: 15px
  font-weight: bold
  text-decoration: none
  cursor: pointer
  transition: background .15s
  &:hover
    background: rgba(70, 155, 92, .4)
  &:focus-visible
    outline: 2px solid #67e787
    outline-offset: 3px
  &:disabled
    cursor: wait
    opacity: .65
.email-section
  margin: 0 auto 18px
  text-align: left
.purchase-terms
  margin-top: 28px
  padding-top: 18px
  border-top: 1px solid rgba(255, 255, 255, .12)
  p
    font-size: 12px
    color: #b9bfce
    margin-bottom: 10px
.description
  text-align: left
@media (max-width: 600px)
  h1
    font-size: 32px
  .payment-methods
    grid-template-columns: 1fr
  .payment-card
    padding: 14px 16px
  .payment-card__top
    margin-bottom: 8px
</style>
